// Time-lapse Progress — the "watch it work over time" clinic-timeline format.
//
// v1 design source (packages/remotion/.../time-progress-layer.tsx):
//   - A SOLID DARK HEADER BAND pinned to the very top of the frame (118px tall,
//     slate rgba(13,15,24,0.86), 46px side padding) carrying the time marker on
//     the LEFT (uppercase Inter 44/800, 0.12em tracking) and the clinic
//     wordmark on the RIGHT (uppercase Inter 26/700, 0.22em, warm cream at 75%).
//   - A serif CAPTION in the lower third (Playfair 58/700, cream #EFE0CE,
//     centred, bottom at 15% up from the base) over a soft bottom gradient.
//   - A slim glowing PROGRESS BAR pinned across the bottom (4.6% up) that fills
//     linearly across the whole video.
//
// The previous v2 authoring reproduced none of that: it emitted two plain
// centre-anchored Inter lines mid-frame (a 76px stroked "hero" at y 0.42 and a
// 40px caption at y 0.56) over a flat 22% scrim — a different layout entirely,
// which is why the owner called it "completely wrong". This version restores
// the band + lower-third-serif structure with generic blocks only:
//   - the band and the bottom gradient are BOTH painted by the single `solid`
//     spine block, whose colour slot takes any CSS `background` string — a
//     hard-stop linear-gradient gives an exact 118px band, and a second comma-
//     separated layer gives v1's soft bottom lift.
//   - the time marker, the wordmark and the caption are plain `text` overlays
//     anchored into the band (left/right) and the lower third (bottom).
//   - the signature progress bar is a `group` overlay whose root is a single
//     `bar` node with `progress: 'elapsed'`. Because the group's duration is
//     `fill`, the block window IS the master, so the bar sweeps 0→1 across the
//     whole video exactly like v1's `interpolate(frame, [0, durationInFrames])`.
//
// FIDELITY GAPS — what v1 does that this doc still cannot say (documented, not
// silently dropped):
//   - v1's mid-video startLabel→endLabel hard swap. `repeat` + `reveal:
//     'sequential'` over a 2-element array is the right SHAPE for it (it would
//     split the block window in half and swap at 50%, near v1's 52%), but there
//     is no slot that yields the two labels: `script-text.role` is a closed
//     enum (hook | body | cta | disclaimer | list) with no start/end marker
//     pair, and `role: 'list'` carries the benefit lines, not the timestamps.
//     Authoring them as a `fixed` two-element array would hardcode copy the
//     planner is supposed to write, so the marker stays a single held `hook`
//     line ("Week 1 → Week 4"). Closing this needs a widened slot role, which
//     is a separate workstream — not something a template can patch around.
//   - the 118px header band is still painted by the `solid` spine block's
//     hard-stop CSS gradient rather than by a `group` stack with a `Frame`.
//     Two things are missing for the honest version: (a) a `Frame` has no
//     width/height — it sizes to its children, so the band's exact 118px would
//     have to be back-computed from font size + paddingY and would drift with
//     any type change; (b) `NodeAlign` has no `space-between`, so one
//     horizontal stack cannot hold the timestamp hard-left and the wordmark
//     hard-right. The gradient gives an exact, content-independent, full-bleed
//     band, so it stays until a Frame can be given an explicit height and a
//     stack a space-between justify.
//   - the 10×44 rounded accent tick to the left of the timestamp. `bar` is a
//     horizontal progress meter (full width of its placed box, fill driven by
//     progress); there is no primitive for a fixed-size decorative rule.
//   - v1's `intro` fade covers the band, the caption AND the bar as one 10-frame
//     ramp. Here each element carries its own `fade-in`, which is equivalent in
//     appearance but authored per element.
//   (CLOSED: v1's serif caption line-height of 1.2 — `TypeStyleOverride`
//   carries `lineHeight` now, so the caption no longer sits at the engine's
//   1.25 default. The two band labels set no line-height in v1, so they are
//   left on the default.)
//   - with no brand kit, v1 falls back to REEL_PALETTE gold (#C9A96A) for the
//     bar fill; v2 binds the fill to the `accent` theme role, whose engine
//     default is #0066FF. That is a theme-default question, not a template one.
//   - THE BAND LABELS DO NOT SHRINK TO FIT. v1 draws each label in a flex row
//     that simply overflows; v2 sizes each an explicit placement box (see
//     TIMESTAMP_WIDTH / WORDMARK_WIDTH). A business name past ~19 uppercase
//     characters will still wrap. Closing this needs a renderer option the
//     `text` block does not have — either `white-space: nowrap` or auto-shrink
//     — not more width, since the band is already fully budgeted.
//   (CLOSED: both labels wrapped onto two lines and overflowed the band. Two
//   causes, both fixed: the placements carried no `width`, and the `text`
//   renderer applied a blanket `maxWidth: 92%` that clipped a shrink-to-fit box
//   below its own content width. The renderer now applies that safety inset
//   only when the author states NO width, and aligns copy to the placement's
//   anchor so an authored width extends inward instead of re-centring.)
const FPS = 30;
const TOTAL_FRAMES = 10 * FPS;
// v1 band geometry, in frame-relative terms (portrait 1080×1920).
const BAND_HEIGHT_PX = 118;
const BAND_CENTRE_Y = BAND_HEIGHT_PX / 2 / 1920; // ≈ 0.031
const BAND_PAD_X = 46 / 1080; // ≈ 0.043
// The two band overlays MUST be sized. A `text` placement with no `width` is a
// shrink-to-fit absolute box, and the renderer's inner safety inset then clips
// it to 92% of its own max-content width — which is, by construction, always
// narrower than the copy. Result: "WEEK 1 → WEEK 4" and the wordmark both
// wrapped onto a second line and overflowed the 118px band.
//
// They sit at opposite ends of ONE band, so their boxes are budgeted to not
// overlap across the 988px between the two 46px pads:
//   timestamp  0.52 → 561px, running 46 → 608
//   wordmark   0.38 → 410px, running 624 → 1034 (right-anchored)
// Measured need at Inter: the timestamp is ~473px (14 uppercase glyphs at 44px
// weight 800 + 0.12em tracking) and "BAYSIDE BEAUTY" ~300px (26px/700 +
// 0.22em), so both carry ≥19% headroom. The wordmark's budget caps a business
// name at roughly 19 uppercase characters before it wraps; a longer name needs
// the band split re-budgeted (or the renderer to gain a nowrap/shrink option),
// not a wider box — there is nowhere left to take the pixels from.
const TIMESTAMP_WIDTH = 0.52;
const WORDMARK_WIDTH = 0.38;
// v1 progress bar geometry: left 7% / right 7% (→ 86% wide, centred) with its
// BOTTOM edge 4.6% up from the base.
const BAR_WIDTH = 0.86;
const BAR_BOTTOM = 0.046;
export const timeProgress1 = {
    id: 'time-progress-1',
    schemaVersion: 2,
    aspectRatios: ['portrait'],
    duration: { kind: 'fixed', frames: TOTAL_FRAMES },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            // Before → after footage, one clip each, cut on the beat so the subject
            // visibly changes across the timestamp.
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 4] },
                    required: true,
                },
                fit: 'contain-blur',
                cuts: { mode: 'beat-synced', beatsPerEdit: 4 },
                fillMode: 'loop',
            },
            // Two background layers in one solid: (1) the hard-edged slate header
            // band across the top 118px, (2) v1's soft bottom lift for the serif
            // caption. The mid-frame stays clean so the footage does the selling.
            {
                kind: 'solid',
                id: 'chrome',
                duration: { kind: 'fill' },
                color: {
                    source: 'fixed',
                    value: 'linear-gradient(180deg, rgba(13,15,24,0.86) 0px, rgba(13,15,24,0.86) 118px, rgba(0,0,0,0) 118px), ' +
                        'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.2) 74%, rgba(0,0,0,0.6) 100%)',
                },
            },
        ],
        overlays: [
            // The time marker, left-aligned inside the header band.
            {
                kind: 'text',
                id: 'timestamp',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'hook' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 44,
                    fontWeight: 800,
                    letterSpacing: 0.12,
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    textShadow: 'none',
                    strokeWidth: 0,
                },
                animation: { entrance: 'fade-in', entranceDurationFrames: 10 },
                placement: {
                    anchor: 'left',
                    x: BAND_PAD_X,
                    y: BAND_CENTRE_Y,
                    width: TIMESTAMP_WIDTH,
                },
                duration: { kind: 'fill' },
                container: 'none',
            },
            // Clinic wordmark, right-aligned inside the same band. Optional: when the
            // org has no name the slot resolves empty and nothing is drawn.
            {
                kind: 'text',
                id: 'wordmark',
                text: {
                    source: 'query',
                    query: { kind: 'brand', field: 'businessName' },
                    required: false,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 26,
                    fontWeight: 700,
                    letterSpacing: 0.22,
                    textTransform: 'uppercase',
                    color: 'rgba(239,224,206,0.75)',
                    textShadow: 'none',
                    strokeWidth: 0,
                },
                animation: { entrance: 'fade-in', entranceDurationFrames: 10 },
                placement: {
                    anchor: 'right',
                    x: 1 - BAND_PAD_X,
                    y: BAND_CENTRE_Y,
                    width: WORDMARK_WIDTH,
                },
                duration: { kind: 'fill' },
                container: 'none',
            },
            // Serif caption in the lower third — the clinic voice. Bottom-anchored at
            // 15% up from the base, matching v1's paddingBottom.
            {
                kind: 'text',
                id: 'caption',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'cta' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontRef: 'playfair',
                    fontSize: 58,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'none',
                    color: '#EFE0CE',
                    // v1 serif caption. (The band labels set none, so they keep the
                    // engine default.)
                    lineHeight: 1.2,
                    textShadow: '0 4px 26px rgba(0,0,0,0.55)',
                    strokeWidth: 0,
                },
                animation: {
                    entrance: 'fade-in',
                    entranceDurationFrames: 12,
                    entranceDelayFrames: 10,
                },
                placement: { anchor: 'bottom', x: 0.5, y: 0.85, width: 0.82 },
                duration: { kind: 'fill' },
                container: 'none',
            },
            // The signature time-lapse bar, pinned across the bottom. `duration:
            // fill` makes the block window the whole master, and `progress:
            // 'elapsed'` fills 0→1 across that window — v1's linear whole-video
            // sweep, with the same track, glowing gradient fill and tip dot (the
            // renderer sizes the dot at 2× the bar height, i.e. v1's 24px).
            {
                kind: 'group',
                id: 'progress',
                duration: { kind: 'fill' },
                placement: {
                    anchor: 'bottom',
                    x: 0.5,
                    y: 1 - BAR_BOTTOM,
                    width: BAR_WIDTH,
                },
                root: {
                    kind: 'bar',
                    progress: 'elapsed',
                    height: 12,
                    radius: 999,
                    trackColor: 'rgba(255,255,255,0.16)',
                    // v1 uses `config.primaryColor` — the org's brand colour — for the
                    // fill, its glow and the tip dot's halo. `accent` is that role in v2.
                    fillColorRole: 'accent',
                    glow: true,
                    tipDot: true,
                    entrance: 'fade-in',
                    entranceDurationFrames: 10,
                },
            },
        ],
    },
    globals: {
        audio: {
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
    },
};
//# sourceMappingURL=time-progress-1.js.map